import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Sparkles, Moon, Sun, Flame, Heart, Eye, Compass, ArrowRight } from "lucide-react";

/**
 * TraditionHub — six-tile "Browse by Tradition" grid for the Landing page.
 *
 * Each tile is a static-art card with the tradition name, a tagline, a live
 * count of matching Elder Founders, and a deep-link to /elders?q=<term>.
 * Backend defines the curated 6 (via /api/providers/tradition-hubs) so the
 * curation can change without a frontend redeploy.
 */
const HUB_ICONS = {
  tarot:      Moon,
  astrology:  Sun,
  hoodoo:     Flame,
  reiki:      Heart,
  ifa:        Compass,
  mediumship: Eye,
};

export default function TraditionHub() {
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/providers/tradition-hubs");
        setHubs(data.hubs || []);
      } catch { /* noop — hide on error */ }
      setLoading(false);
    })();
  }, []);

  if (loading || hubs.length === 0) return null;

  return (
    <section className="mt-20" data-testid="tradition-hub-section">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-mystic text-secondary inline-flex items-center gap-2">
            <Sparkles size={11} strokeWidth={2} /> Browse the lineages
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl mt-2">Find your tradition</h2>
        </div>
        <Link
          to="/elders"
          className="text-xs uppercase tracking-[0.22em] text-secondary hover:text-secondary/80 inline-flex items-center gap-1.5"
          data-testid="tradition-hub-view-all"
        >
          View all founders <ArrowRight size={11} />
        </Link>
      </div>

      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}
        data-testid="tradition-hub-grid"
      >
        {hubs.map((h) => {
          const Icon = HUB_ICONS[h.key] || Sparkles;
          return (
            <Link
              key={h.key}
              to={`/elders?q=${encodeURIComponent(h.q)}`}
              className="relative overflow-hidden block border border-foreground/15 rounded-sm p-6 hover:border-secondary/60 transition-all group hover:translate-y-[-2px]"
              data-testid={`tradition-hub-tile-${h.key}`}
              style={{
                background:
                  `radial-gradient(circle at 80% 10%, ${h.accent}33, transparent 55%), ` +
                  `linear-gradient(155deg, ${h.accent}1A 0%, transparent 60%), #15102A`,
              }}
            >
              <div
                className="absolute right-4 top-4 opacity-30 group-hover:opacity-60 transition-opacity"
                style={{ color: h.accent }}
              >
                <Icon size={56} strokeWidth={1.25} />
              </div>

              <div className="relative">
                <div className="label-mystic" style={{ color: h.accent }}>
                  Tradition
                </div>
                <h3 className="font-serif text-2xl mt-1.5 text-foreground" data-testid={`tradition-hub-title-${h.key}`}>
                  {h.title}
                </h3>
                <p className="text-sm text-foreground/65 mt-1 italic">{h.tagline}</p>

                <div className="flex items-end justify-between mt-8 gap-3">
                  <div>
                    <div className="label-mystic text-foreground/55">Founders</div>
                    <div className="font-serif text-3xl text-foreground" data-testid={`tradition-hub-count-${h.key}`}>
                      {h.count}
                    </div>
                  </div>
                  <div
                    className="text-[11px] uppercase tracking-[0.22em] inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all"
                    style={{ color: h.accent }}
                  >
                    Explore <ArrowRight size={11} />
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
